import { Alert, Button, Checkbox, Dropzone, DropzoneRejectionCode, FileInput, FormField } from '@percent/lemonade'
import styles from './AgentVerificationDocuments.module.scss'
import { useTranslation } from 'react-i18next'
import { FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'
import { useMutation } from '@percent/cause-dashboard/common/hooks'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { AgentVerificationDocumentsProps } from './AgentVerificationDocuments.types'
import { PartnerBanner } from './partnerBanner/PartnerBanner'
import { useState } from 'react'
import { LocaleKey } from '@percent/cause-dashboard/i18n/config'
import { HELP_LINK } from '@percent/cause-dashboard/constants/support'
import { PRIVACY_POLICY_LINK, TERMS_LINK } from '@percent/cause-dashboard/constants/legal'
import { documentTypesFor } from './documentTypes'

// Extensions, not mimes: Chrome and Firefox often report an empty type for a HEIC pick.
const ALLOWED_FILE_TYPES = '.jpg,.jpeg,.png,.heic,.heif,.pdf'
const MAX_FILES = 5
const MAX_FILE_SIZE_IN_MB = 5

const validateFileSize = (files?: File[] | null) =>
  files ? files?.filter(file => file?.size > MAX_FILE_SIZE_IN_MB * 1024 * 1024)?.length === 0 : true

export const AgentVerificationDocuments = ({
  agentVerificationId,
  agentVerificationToken,
  onDocumentsSubmitted,
  organisationName,
  header,
  hideDefaultHeading = false,
  body,
  useDropzone = false,
  partnerBanner,
  programType
}: AgentVerificationDocumentsProps) => {
  const [hasError, setHasError] = useState(false)
  const { t } = useTranslation()
  const { agentVerificationService } = useServices()

  const [{ isLoading }, { apiFunc: submitDocuments }] = useMutation(
    agentVerificationService.postAgentVerificationDocuments,
    () => onDocumentsSubmitted(),
    () => setHasError(true)
  )

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      files: null,
      termsAndConditions: false
    },
    validationSchema: Yup.object().shape({
      files: Yup.array()
        .nullable()
        .min(1, '')
        .max(MAX_FILES, 'errorMessage.maxFilesLimit')
        .test('validate-file-size', 'errorMessage.maxFileSize', validateFileSize),
      termsAndConditions: Yup.bool().oneOf([true])
    }),
    onSubmit: async values => {
      if (values.files) {
        const files = values.files as File[]
        const formData = new FormData()

        files.forEach((file, index) => formData.append(`file${index}`, file))

        submitDocuments({
          agentVerificationId,
          agentVerificationToken,
          formData
        })
      }
    }
  })

  const { values, setFieldValue, isValid, errors, submitForm } = formik
  const uploadDocumentationText = t('agentVerificationDocuments.uploadDocumentation')

  const toggleTermsAndConditions = () => setFieldValue('termsAndConditions', !values.termsAndConditions)

  return (
    <FormikProvider value={formik}>
      <div className={styles.documentsContainer} data-testid="agent-verification-documents-form">
        <div className={styles.cardHeader}>
          <PartnerBanner banner={partnerBanner} />
          {header}
          {!hideDefaultHeading && <h5>{uploadDocumentationText}</h5>}
        </div>

        <div className={styles.scrollableContent}>
          {hasError && (
            <Alert variant="error" title={t('agentVerificationDocuments.errorMessage.title')}>
              {t('agentVerificationDocuments.errorMessage.description')}{' '}
              <a href={HELP_LINK} target="_blank" rel="noreferrer">
                {t('agentVerificationDocuments.errorMessage.helpCenter')}
              </a>
            </Alert>
          )}

          {body ?? (
            <>
              <div>
                <p>{t('agentVerificationDocuments.helpUsVerifyUpdated', { organisationName })}</p>
                <ul>
                  {documentTypesFor(programType).map(({ key }) => (
                    <li key={key}>{t(key)}</li>
                  ))}
                </ul>
              </div>
              <p>{t('agentVerificationDocuments.note', { organisationName })}</p>
            </>
          )}

          <div className={styles.fileInputWrapper}>
            <FormField
              label={uploadDocumentationText}
              necessity="required"
              status={errors.files ? 'danger' : 'default'}
              statusMessage={errors.files && t(errors.files as LocaleKey)}
            >
              {useDropzone ? (
                <Dropzone
                  title={t('agentVerificationDocuments.reupload.dropzone.title')}
                  description={t('agentVerificationDocuments.reupload.dropzone.description', {
                    maxSize: MAX_FILE_SIZE_IN_MB
                  })}
                  files={(values.files as File[] | null) ?? []}
                  onChange={files => setFieldValue('files', files)}
                  maxFiles={MAX_FILES}
                  maxSize={MAX_FILE_SIZE_IN_MB * 1024 * 1024}
                  testId="documentation-dropzone"
                  getErrorMessage={({ fileName, code, maxSizeFormatted, acceptedFileTypes }) => {
                    const messages: Record<DropzoneRejectionCode, string> = {
                      'file-too-large': t('agentVerificationDocuments.reupload.dropzone.errors.fileTooLarge', {
                        fileName,
                        maxSize: maxSizeFormatted
                      }),
                      'file-invalid-type': t('agentVerificationDocuments.reupload.dropzone.errors.fileInvalidType', {
                        fileName,
                        fileTypes: acceptedFileTypes
                      }),
                      'too-many-files': t('agentVerificationDocuments.reupload.dropzone.errors.tooManyFiles', {
                        maxFiles: MAX_FILES
                      })
                    }

                    return messages[code]
                  }}
                />
              ) : (
                <FileInput
                  fileUploadLabel={t('typography.selectFiles')}
                  placeholder={t('agentVerificationDocuments.fileInputPlaceholder', { filesNumber: MAX_FILES })}
                  onChangeForMultiple={files => {
                    setFieldValue('files', files)
                  }}
                  accept={ALLOWED_FILE_TYPES}
                  allowMultiple
                  dataTestId="documentation-files-button"
                />
              )}
            </FormField>
          </div>

          {useDropzone && (
            <p>{t('agentVerificationDocuments.reupload.dropzone.fileCountHint', { filesNumber: MAX_FILES })}</p>
          )}

          <div className={styles.termsAndConditionsWrapper}>
            <p>{t('agentVerificationDocuments.acceptTerms')}</p>
            <Checkbox
              active={values.termsAndConditions}
              variant="default"
              label={
                <span>
                  {t('typography.agreeToThe')}{' '}
                  <a href={TERMS_LINK} target="_blank" rel="noreferrer">
                    {t('typography.terms')}
                  </a>
                  {' & '}
                  <a href={PRIVACY_POLICY_LINK} target="_blank" rel="noreferrer">
                    {t('typography.privacyPolicy')}
                  </a>
                </span>
              }
              name="termsAndConditions"
              dataTestId="terms-and-conditions-checkbox"
              onChange={toggleTermsAndConditions}
            />
          </div>
        </div>

        <div className={styles.submitButtonWrapper}>
          <div className={styles.submitButton}>
            <Button
              onClick={submitForm}
              loading={isLoading}
              disabled={!values.files || !isValid || isLoading}
              data-testid="upload-documentation-button"
              stretch
            >
              {t('agentVerificationDocuments.submitDocumentation')}
            </Button>
          </div>
        </div>
      </div>
    </FormikProvider>
  )
}
